/**
 * The 48 hour re-open mark for a filed sub request, and the version a filing is
 * filed THROUGH. Pure arithmetic, deliberately in a module of its own.
 *
 * WHY IT MOVED HERE (2026-09-05). These live in subs.ts, which imports
 * lib/firebase, so messageInboxStateCore - the firebase-free half of the inbox
 * state machinery - could not reach them. messageInboxSourceVersionMs
 * therefore had NO `sub` branch and fell through to `return 0`, while the
 * caller passed the real version, and assertExpectedInboxVersion compares the
 * two with strict equality. Every "Done with this" on a sub request threw.
 *
 * Copying the arithmetic into the core instead would have left two definitions
 * of a 48 hour rule that must agree forever, which is the failure this repo
 * keeps writing tests about. One definition, imported by both. subs.ts
 * re-exports these so every existing caller is untouched.
 */

/** Hours before a class starts that an UNCOVERED request comes back. */
export const SUB_REOPEN_HOURS_BEFORE = 48;

const STUDIO_TIME_ZONE = 'America/Chicago';
const studioWallClock = new Intl.DateTimeFormat('en-US', {
  timeZone: STUDIO_TIME_ZONE,
  hourCycle: 'h23',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
});

function studioWallClockMs(utcMs: number): number {
  const parts = Object.fromEntries(
    studioWallClock.formatToParts(new Date(utcMs)).map((part) => [part.type, part.value])
  );
  return Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour),
    Number(parts.minute),
    Number(parts.second)
  );
}

/**
 * The absolute instant of a wall-clock time on a studio day, independent of the
 * device zone. `minutesOfDay` is minutes past midnight, so 0 is the start of
 * the day and 17 * 60 is a 5:00 PM class.
 *
 * ONE SOLVER, generalized from studioDateStartMs when the sub board's urgency
 * badge needed a class HOUR rather than a day start. Adding minutes onto a day
 * start would have been an hour wrong on the two days a year the clocks move,
 * which is the sort of quiet almost-right this file exists to stop.
 *
 * Returns null when the wall time does not exist, which is the honest answer
 * for the hour skipped on the spring-forward morning.
 */
export function studioDateTimeMs(dateKey: string, minutesOfDay = 0): number | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateKey)) return null;
  if (!Number.isFinite(minutesOfDay) || minutesOfDay < 0 || minutesOfDay >= 24 * 60) return null;
  const [y, m, d] = dateKey.split('-').map(Number);
  const dayWallMs = Date.UTC(y, m - 1, d, 0, 0, 0);
  const day = new Date(dayWallMs);
  if (day.getUTCFullYear() !== y || day.getUTCMonth() !== m - 1 || day.getUTCDate() !== d) {
    return null;
  }
  const targetWallMs = dayWallMs + minutesOfDay * 60_000;
  // Solve UTC -> Chicago wall time instead of asking the browser's local Date
  // constructor. Two passes cover both CST and CDT offsets, including dates
  // adjacent to a DST transition.
  let candidate = targetWallMs;
  for (let pass = 0; pass < 3; pass += 1) {
    candidate += targetWallMs - studioWallClockMs(candidate);
  }
  return studioWallClockMs(candidate) === targetWallMs ? candidate : null;
}

/** Midnight for an ISO date in the studio's zone, independent of the device zone. */
export function studioDateStartMs(dateKey: string): number | null {
  return studioDateTimeMs(dateKey, 0);
}

/**
 * When a request filed away with "Done with this" should come BACK.
 *
 * Bobbie, 2026-09-04: "I need a done with this. Button. Also it should show up
 * again 48 hours before for all teachers and directors and managers if not one
 * has covered it."
 *
 * Returned as a `contentChangedAtMs` for isFiledToPast(), which already treats
 * a filing as stale once the content changed after it was filed. So this needs
 * no new mechanism and no stored state: it is null while the request is still
 * more than 48 hours out (the filing holds), and becomes the 48-hour mark once
 * that moment has passed (every filing made BEFORE it goes stale, and the card
 * returns). Filing it again after the escalation sticks, because that filing is
 * newer than the mark.
 *
 * A COVERED or CANCELLED request never comes back. It is not work any more, and
 * a card that reappears for something already handled is the noise this whole
 * inbox is built to avoid.
 */
export function subRequestReopenAtMs(
  request: { status?: string; date?: string; timeHint?: string; claimedBy?: string | null },
  nowMs: number
): number | null {
  if (request.status !== 'open' || request.claimedBy) return null;
  if (!request.date) return null;
  // Midnight at the START of the class day, in STUDIO time (the app runs on
  // the studio's zone; CLAUDE.md). A timeHint is free text and is deliberately
  // not parsed here: 48 hours before the day is the honest floor, and reading
  // "5:00 PM" wrong would move a real deadline.
  const dayStart = studioDateStartMs(request.date);
  if (dayStart == null) return null;
  const reopenAt = dayStart - SUB_REOPEN_HOURS_BEFORE * 60 * 60 * 1000;
  return nowMs >= reopenAt ? reopenAt : null;
}

/**
 * The version a filed sub request is filed THROUGH, which is what makes
 * "Done with this" expire at the 48 hour mark without storing anything new.
 *
 * isFiledThrough() keeps a card filed while filedThroughMs >= this number, so
 * the moment this jumps to the reopen mark, every filing made before it stops
 * covering the card and it comes back. Filing it again afterwards stores the
 * mark itself and sticks, because the comparison is >=.
 *
 * updatedAt rides along for the ordinary reason every other kind does: if the
 * requester edits the request after you filed it, that is new information and
 * the card should return.
 */
export function subInboxVersionMs(
  request: { status?: string; date?: string; claimedBy?: string | null },
  updatedAtMs: number | null,
  nowMs: number
): number {
  const reopen = subRequestReopenAtMs(request, nowMs);
  return Math.max(updatedAtMs ?? 0, reopen ?? 0);
}
